import { useTranslation } from "react-i18next";
import { panelStyle } from "../../../shared/ui/card-frame";
import type { CategorySeo } from "../seo/category-seo";

/**
 * "About {city}" / "Why {city}" — pewter, like the rest of the hub.
 *
 * Was frosted glass (white/55 + backdrop-blur) with a lilac blur blob in the
 * corner. The blob was there to give a flat glass panel something to catch;
 * the pewter band already does that, so it goes.
 *
 * Which light, across the whole hub (owner, 2026-09-07 — "see who deserves
 * topLit and who deserves pewter"):
 *
 *   pewter  a wide panel you stop and read, one or two to a row — About, Why,
 *           Where to go, Best time. There is room for the band to turn, and
 *           the turn is the whole point of the house frame.
 *   topLit  a repeating grid of small cards — the FAQ rows, the three article
 *           cards, the four nearby-city cards. Four bands side by side each
 *           catching light at a different corner reads as glitter; one light
 *           from above keeps the row calm and lets the cards be a set.
 *
 * Chips and one-line links get neither: white with a single navy hairline.
 * The band never goes on something you press (owner, 2026-09-05).
 */

const NAVY = "#110E39";
const MUTED = "#5a5d77";

interface CategoryIntroProps {
  sections: CategorySeo["sections"];
}

export function CategoryIntro({ sections }: CategoryIntroProps) {
  const { t } = useTranslation();
  if (sections.length === 0) return null;

  return (
    <section aria-label={t("activity.overview")} className="py-10">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sections.map((s, i) => (
          <article
            key={s.title}
            className={`p-6 ${i === 0 ? "md:col-span-2 lg:col-span-2" : ""}`}
            style={panelStyle(22)}
          >
            <h2
              className="text-[20px] font-semibold leading-tight sm:text-[22px]"
              style={{ color: NAVY, fontFamily: "var(--font-geist)", letterSpacing: "-0.02em" }}
            >
              {s.title}
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed sm:text-[15px]" style={{ color: MUTED }}>
              {s.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
